import { z } from 'zod';

type StripDefault<T> = T extends z.ZodDefault<infer Inner> ? StripDefault<Inner> : T;
export type PatchShape<Shape extends z.ZodRawShape> = { [K in keyof Shape]: z.ZodOptional<StripDefault<Shape[K]>> };

/**
 * PATCH body schema derived from a create schema: every field optional and every `.default()` removed.
 * (Zod's `.partial()` keeps defaults, which would silently reset omitted fields — e.g. demote a role.)
 */
export function patchSchema<Shape extends z.ZodRawShape>(schema: z.ZodObject<Shape>): z.ZodObject<PatchShape<Shape>> {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(schema.shape)) {
    let inner = field as z.ZodType;
    while (inner instanceof z.ZodDefault) inner = inner.removeDefault() as z.ZodType;
    shape[key] = inner.optional();
  }
  return z.object(shape) as unknown as z.ZodObject<PatchShape<Shape>>;
}

export const idSchema = z.uuid();
export type Id = z.infer<typeof idSchema>;

export const slugSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Faqat kichik lotin harflari, raqamlar va "-"');

export const LOCALES = ['uz-Latn', 'uz-Cyrl', 'ru', 'en'] as const;
export const localeSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof localeSchema>;

export const THEMES = ['dark', 'light'] as const;
export const themeSchema = z.enum(THEMES);
export type Theme = z.infer<typeof themeSchema>;

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  q: z.string().trim().max(200).optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** RFC 9457 problem details — every API error has this shape. */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  code?: string;
  errors?: Array<{ path: string; message: string }>;
}
